import { Card, Col, Row } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { MATCHING, SEPARATOR_CHARACTER } from '@common/constants';
import {
    matchingCheckSkipToken,
    useEquivalentProductMatching,
} from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatchingSummaryAlerts';
import { useLazyEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useLazyEquivalentProductMatchingSummaryAlerts';
import { StoreModel } from '@common/models';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import {
    ConfirmationDialogWithBanner,
    DialogBanner,
} from '@core/components/confirmation-dialog/ConfirmationDialogWithBanner';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import useUpdateWithHistory from '@core/hooks/useUpdateWithHistory';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';

import LocationOutlinedIcon from '/public/assets/icons/location-outlined.svg';

import './zip-code-stores.scss';

interface RetailStoresProps {
    stores: StoreModel[];
    selectedStore?: StoreModel;
    confirmStoreChange: boolean;
    onClick?: (store: StoreModel | undefined) => void;
    isFetching: boolean;
    hidden?: boolean;
}

interface StoreItemProps {
    key: number;
    store: StoreModel;
    isSelected?: boolean;
    onStoreClick: (store: StoreModel) => unknown;
}

const StoreItem = ({ store, isSelected, onStoreClick }: StoreItemProps) => {
    const completeAddress = store.address?.split(SEPARATOR_CHARACTER);
    const address = completeAddress ? completeAddress[0] : '';

    return (
        <Col>
            <Card
                className={classNames('item-store single-action-card', {
                    selected: isSelected,
                })}
                data-testid="store-card"
                onClick={() => onStoreClick(store)}
            >
                <div className="bolder">{store.companyName}</div>
                <div>{address}</div>
                <div className="city-info">
                    {store.city}
                    {SEPARATOR_CHARACTER} {store.state} {store.zip}
                </div>
                <div className="bolder">{store.distance} mi.</div>
            </Card>
        </Col>
    );
};

/**
 * @param stores array of stores to show
 * @param selectedStore currently selected store (for highlighting)
 * @param onClick action to take after selecting a new store (will be called after confirming the change)
 * @param isFetching are any of the dependencies fetching?
 * @param confirmStoreChange should we confirm a store change after the user clicks?
 * @param hidden whether to hide the list of stores
 */
export function RetailStores({
    stores,
    onClick,
    isFetching,
    confirmStoreChange,
    hidden,
}: RetailStoresProps): JSX.Element {
    const { t } = useTranslation();

    const { selectedValue: selectedStore } = useSelectedStore();

    const [isChangeStoreConfirmationModalOpen, setIsChangeStoreConfirmationModalOpen] = useState(false);
    const [temporaryStore, setTemporaryStore] = useState<StoreModel | undefined>();
    const { retailerSettings } = useRetailerSettings();
    const [_showLoader, setShowLoader] = useState(false);
    const { setSelectedStoreWithHistory } = useUpdateWithHistory();

    const { replaceProductsWithEquivalents } = useEquivalentProductMatching();
    const { LoaderOnAlerts, replaceIfNoAlerts } = useLazyEquivalentProductMatchingSummaryAlerts();
    const { trackSelectStore } = useAnalyticsEvents();

    const isEquivalentProductMatchingWithFunctionsEnabled = retailerSettings?.enableMatching === MATCHING.RICARDO;

    const { MessageWithAlerts, userMustCancel } = useEquivalentProductMatchingSummaryAlerts(
        temporaryStore && isEquivalentProductMatchingWithFunctionsEnabled
            ? { storeId: temporaryStore?.companyId }
            : matchingCheckSkipToken
    );

    const onStoreClick = async (clickedStore: StoreModel) => {
        if (clickedStore.companyId === selectedStore?.companyId) {
            return;
        }
        // This is the original behavior, get it out of the way
        if (!isEquivalentProductMatchingWithFunctionsEnabled) {
            if (confirmStoreChange) {
                setTemporaryStore(clickedStore);
                setIsChangeStoreConfirmationModalOpen(true);
            } else {
                setSelectedStoreWithHistory(clickedStore);
                trackSelectStore({ store: clickedStore });
                if (onClick) {
                    onClick(clickedStore);
                }
            }
        }
        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            replaceIfNoAlerts({
                targetParameters: { storeId: clickedStore.companyId },
                onAlertsCallback: () => {
                    setTemporaryStore(clickedStore);
                    setIsChangeStoreConfirmationModalOpen(true);
                },
                onNoAlertsCallback: () => {
                    setSelectedStoreWithHistory(clickedStore);
                    trackSelectStore({ store: clickedStore });
                    if (onClick) {
                        onClick(clickedStore);
                    }
                },
            });
            flushSync(() => {
                setShowLoader(true);
            });
        }
    };

    const onConfirmModal = async () => {
        if (!temporaryStore) {
            console.error('Error TemporaryStore is undefined');
            return;
        }

        setIsChangeStoreConfirmationModalOpen(false);
        await replaceProductsWithEquivalents({ storeId: temporaryStore?.companyId });
        setSelectedStoreWithHistory(temporaryStore);
        if (onClick) {
            onClick(temporaryStore);
        }
    };

    const onCancelModal = () => {
        setIsChangeStoreConfirmationModalOpen(false);
    };

    if (hidden) {
        return <></>;
    }
    return (
        <>
            <LoaderOnAlerts />
            {isFetching || stores.length ? (
                <Row className="container-stores" gutter={[10, 10]}>
                    {stores.map((store) => (
                        <StoreItem
                            onStoreClick={onStoreClick}
                            key={store.companyId}
                            store={store}
                            isSelected={selectedStore?.companyId === store.companyId}
                        />
                    ))}
                </Row>
            ) : (
                <div className="StoreAvailability StoreAvailability__not-available">
                    {t('ADDRESS_INFORMATION.NO_STORE')}
                </div>
            )}
            {isEquivalentProductMatchingWithFunctionsEnabled && userMustCancel ? (
                <ConfirmationDialog
                    visible={isChangeStoreConfirmationModalOpen}
                    onCancel={onCancelModal}
                    title={t('EQUIVALENT_PRODUCT_MATCHING.STORE.ALERT_TITLE')}
                    content={<MessageWithAlerts />}
                />
            ) : null}
            {isEquivalentProductMatchingWithFunctionsEnabled && !userMustCancel ? (
                <ConfirmationDialogWithBanner
                    banner={
                        <DialogBanner
                            icon={<LocationOutlinedIcon />}
                            bannerText={t('EQUIVALENT_PRODUCT_MATCHING.ALERTS_TEXT')}
                        />
                    }
                    visible={isChangeStoreConfirmationModalOpen}
                    onCancel={onCancelModal}
                    onConfirm={onConfirmModal}
                    icon={<LocationOutlinedIcon />}
                    title={t('ADDRESS_INFORMATION.CHANGE_STORES_CONFIRMATION_MODAL.TITLE')}
                    content={<MessageWithAlerts />}
                />
            ) : null}
            {!isEquivalentProductMatchingWithFunctionsEnabled ? (
                <ConfirmationDialog
                    visible={isChangeStoreConfirmationModalOpen}
                    onCancel={() => setIsChangeStoreConfirmationModalOpen(false)}
                    onConfirm={onConfirmModal}
                    icon={<LocationOutlinedIcon />}
                    title={t('ADDRESS_INFORMATION.CHANGE_STORES_CONFIRMATION_MODAL.TITLE')}
                    content={t('ADDRESS_INFORMATION.CHANGE_STORES_CONFIRMATION_MODAL.DESCRIPTION')}
                />
            ) : null}
        </>
    );
}
